import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { StepperModule } from 'primeng/stepper';
import { ButtonModule } from 'primeng/button';
import { RadioButtonModule } from 'primeng/radiobutton';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { CheckboxModule } from 'primeng/checkbox';

// Import all step components and their interfaces
import { BasicInfoComponent } from './steps/basic-info/basic-info.component';
import { IndexInsertStepComponent } from './steps/index-insert/index-insert.component';
import { AdditionalNamesComponent, AdditionalName } from './steps/additional-names/additional-names.component';
import { EmailsComponent, Email } from './steps/emails/emails.component';
import { AddressesComponent, Address } from './steps/addresses/addresses.component';
import { PhoneNumbersComponent, PhoneNumber } from './steps/phone-numbers/phone-numbers.component';
import { PaymentInfoComponent, PaymentInfo } from './steps/payment-info/payment-info.component';
import { RelationsComponent, Relation } from './steps/relations/relations.component';
import { MoveCodesComponent, MoveCode } from './steps/move-codes/move-codes.component';
import { MoveCode6111Component, MoveCode6111 } from './steps/move-code6111/move-code6111.component';
import { UrlsComponent, UrlRec } from './steps/urls/urls.component';
import { Profiler, ProfilersComponent } from './steps/profilers/profilers.component';
import { SummaryComponent } from './steps/summary/summary.component';
import { SelectItem } from '../../../models/selected-item.model';


@Component({
    selector: 'app-create-account',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        StepperModule,
        ButtonModule,
        RadioButtonModule,
        InputTextModule,
        DropdownModule,
        CheckboxModule,
        ToastModule,
        BasicInfoComponent,
        IndexInsertStepComponent,
        AdditionalNamesComponent,
        EmailsComponent,
        AddressesComponent,
        PhoneNumbersComponent,
        // PaymentInfoComponent,
        // RelationsComponent,
        // MoveCodesComponent,
        // MoveCode6111Component,
        ProfilersComponent,
        UrlsComponent,
        SummaryComponent
    ],
    templateUrl: './create-account.component.html',
    styleUrls: ['./create-account.component.scss'],
    providers: [MessageService]
})
export class CreateAccountComponent implements OnInit {

    step: number = 1;

    // Data for Basic Info step
    entityType: SelectItem | null = null;
    businessName: string = '';
    businessType: string | null = null;
    businessNumber: string = '';
    isActive: boolean = true;

    // Data for Additional Names step
    additionalNames: AdditionalName[] = [];

    // Data for the Profiles step
    profiles: Profiler[] = [];

    // Data for Emails step
    emails: Email[] = [];

    // Data for Addresses step
    addresses: Address[] = [];

    // Data for Phone Numbers step
    phoneNumbers: PhoneNumber[] = [];

    // Data for Payment Info step
    paymentInfo: PaymentInfo[] = [];

    // Data for Relations step
    relations: Relation[] = [];

    // Data for Move Codes step
    moveCodes: MoveCode[] = [];
    useDefaultMoveCodes: boolean = true;

    moveCode6111: MoveCode6111[] = [];
    useDefaultMoveCode6111: boolean = true;

    urls: UrlRec[] = [];


    constructor(private messageService: MessageService) { }

    ngOnInit(): void {
        // Initialization logic if needed
    }

    /**
     * Handles the 'nextStep' event from any child component.
     * Activates the next step in the stepper.
     * @param activateCallback The callback function from p-stepper to activate the next step.
     * @param currentStep The current step number from which the next action was initiated.
     */
    onNext(activateCallback: (step: number) => void, currentStep: number, stepIncrement: number = 1): void {
        activateCallback(currentStep + stepIncrement);
    }

    /**
     * Handles the 'skipToLastStep' event from any child component.
     * Jumps to the last step (summary panel).
     */
    onSkipToLastStep(activateCallback: (step: number) => void): void {
        activateCallback(9); // 9 is the summary step
    }

    /**
     * Handles the 'prevStep' event from any child component.
     * Activates the previous step in the stepper.
     * @param activateCallback The callback function from p-stepper to activate the previous step.
     * @param currentStep The current step number from which the back action was initiated.
     */
    onPrevious(activateCallback: (step: number) => void, currentStep: number, stepDecrement: number = 1): void {
        const newStep = currentStep - stepDecrement;
        if (newStep >= 1) {
            activateCallback(newStep);
        } else {
            activateCallback(1);
        }
    }

    /**
     * Placeholder for the save account logic.
     * In a real app, this would collect all form data and send it to an API.
     */
    saveAccount(): void {
        const customerSupplierData = {
            entityType: this.entityType,
            businessName: this.businessName,
            businessType: this.businessType,
            businessNumber: this.businessNumber,
            isActive: this.isActive,
            additionalNames: this.additionalNames,
            profiles: this.profiles,
            emails: this.emails,
            addresses: this.addresses,
            phoneNumbers: this.phoneNumbers,
            paymentInfo: this.paymentInfo,
            relations: this.relations,
            moveCodes: this.moveCodes,
            useDefaultMoveCodes: this.useDefaultMoveCodes,
            moveCode6111: this.moveCode6111,
            useDefaultMoveCode6111: this.useDefaultMoveCode6111,
            urls: this.urls
        };

        console.log('Attempting to save:', customerSupplierData);

        setTimeout(() => {
            this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Customer/Supplier data saved successfully!',
                life: 3000
            });
            console.log('Simulated API Save Success:', customerSupplierData);
            this.resetForm();
        }, 1500);
    }

    /**
     * Placeholder for the cancel logic.
     * Resets the form and navigates back to the first step.
     */
    cancel(): void {
        this.messageService.add({
            severity: 'info',
            summary: 'Cancelled',
            detail: 'Account creation cancelled.',
            life: 3000
        });
        this.resetForm();
    }

    /**
     * Resets all form fields to their initial state.
     */
    resetForm(): void {
        this.step = 1;
        this.entityType = null;
        this.businessName = '';
        this.businessType = null;
        this.businessNumber = '';
        this.isActive = true;
        this.additionalNames = [];
        this.profiles = [];
        this.emails = [];
        this.addresses = [];
        this.phoneNumbers = [];
        this.paymentInfo = [];
        this.relations = [];
        this.moveCodes = [];
        this.useDefaultMoveCodes = true;
        this.moveCode6111 = [];
        this.useDefaultMoveCode6111 = true;
        this.urls = [];
    }

    onEntityTypeChanged(newType: SelectItem | null): void {
        this.entityType = newType;
    }

    /**
     * Returns true if the given entityType is Customer (code === '100').
     */
    isEntityTypeCustomer(entityType: SelectItem | null): boolean {
        return !!entityType && typeof entityType === 'object' && entityType.code === '100';
    }
}
